// ─── ThemeToggle ───────────────────────────────────────────────────────────────
// Three-state toggle (Light / System / Dark) shown as a compact button group.

import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { cn } from '../../utils/helpers';
import type { ThemePreference } from '../../store/themeStore';

interface Option {
  value: ThemePreference;
  label: string;
  icon: React.ReactNode;
}

const options: Option[] = [
  {
    value: 'light',
    label: 'Light',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="4" strokeWidth="1.8" />
        <path strokeLinecap="round" strokeWidth="1.8"
          d="M12 3v1.5M12 19.5V21M3 12h1.5M19.5 12H21M5.6 5.6l1.06 1.06M17.34 17.34l1.06 1.06M5.6 18.4l1.06-1.06M17.34 6.66l1.06-1.06" />
      </svg>
    ),
  },
  {
    value: 'system',
    label: 'System',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="12" rx="2" strokeWidth="1.8" />
        <path strokeLinecap="round" strokeWidth="1.8" d="M8 20h8M10 17v3M14 17v3" />
      </svg>
    ),
  },
  {
    value: 'dark',
    label: 'Dark',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8"
          d="M21 12.79A9 9 0 0 1 11.21 3 7 7 0 1 0 21 12.79z" />
      </svg>
    ),
  },
];

interface Props {
  className?: string;
  /** Compact = single icon button that flips through the three modes (mobile-friendly) */
  compact?: boolean;
}

const ThemeToggle: React.FC<Props> = ({ className, compact = false }) => {
  const { preference, setPreference, resolved, toggle } = useTheme();

  if (compact) {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={`Switch to ${resolved === 'dark' ? 'light' : 'dark'} mode`}
        title={`Theme: ${preference}`}
        className={cn(
          'inline-flex items-center justify-center w-9 h-9 rounded-lg',
          'text-textMuted hover:text-textMain hover:bg-surfaceMuted transition-colors',
          className
        )}
      >
        {resolved === 'dark' ? options[0].icon : options[2].icon}
      </button>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={cn(
        'inline-flex items-center p-0.5 h-8 rounded-md border border-borderLight bg-surfaceMuted',
        className
      )}
    >
      {options.map((opt) => {
        const active = preference === opt.value;
        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={active}
            type="button"
            onClick={() => setPreference(opt.value)}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 px-2 h-7 rounded text-[11px] font-semibold uppercase tracking-wider transition-all',
              active
                ? 'bg-surface text-textMain shadow-sm'
                : 'text-textMuted hover:text-textMain'
            )}
            title={opt.label}
          >
            <span className="shrink-0">{opt.icon}</span>
            <span className="hidden sm:inline">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default ThemeToggle;
